import React from "react";
import { H2, H3, SystemMessage, Text } from "@shoptet/ui";
import { Trans, useTranslation } from "react-i18next";
import { Accordion } from "../../elements/Accordion";
import { AudienceSignals } from "../../elements/AdvancedSetup/AudienceSignals";
import { BiddingScheme } from "../../elements/AdvancedSetup/BiddingScheme";
import { Deactivate } from "../../elements/AdvancedSetup/Deactivate";
import { ExcludeProducts } from "../../elements/AdvancedSetup/ExcludeProducts";
import { ExtendedConversions } from "../../elements/AdvancedSetup/ExtendedConversions";
import { PrimaryColor } from "../../elements/AdvancedSetup/PrimaryColor";
import { CategorySelectionWrapper } from "../../elements/CategoriesSelection";
import { SmallLoader } from "../../elements/SmallLoader";
import { SuggestionInputForm } from "../../elements/SuggestionInputForm";
import {
  AdSystemT,
  useAdSystemCampaignStateQuery,
  useLastSuggestionRequestQuery,
  useOrganizationStateQuery,
  useProductExclusionQuery,
} from "../../graphql/generated/graphql";
import { getAdSystemCampaignState } from "../../utils/getAdSystemCampaignState";

const GoogleSettings = () => {
  const { t } = useTranslation("translation", { keyPrefix: "Settings Page" });
  const { t: tSuggestions } = useTranslation("translation", { keyPrefix: "Wizard Suggestion Generator Page" });
  const { data: lastSuggestionRequestData, loading: lastSuggestionRequestLoading } = useLastSuggestionRequestQuery({
    fetchPolicy: "network-only",
  });
  const { data: organizationStateData } = useOrganizationStateQuery({
    variables: {
      dateTo: new Date().toISOString().split("T")[0],
    },
  });
  const { data: campaignStateData } = useAdSystemCampaignStateQuery({
    variables: {
      adSystem: AdSystemT.GoogleAdsT,
      dateTo: new Date().toISOString().split("T")[0],
    },
  });

  const { data: productExclusionData } = useProductExclusionQuery({
    variables: { adSystem: AdSystemT.GoogleAdsT },
  });
  const campaignState = getAdSystemCampaignState(campaignStateData, AdSystemT.GoogleAdsT);
  const hasOptimizations = (organizationStateData?.organization?.campaignOptimizations?.length ?? 0) > 0;
  const totalProducts = productExclusionData?.organization?.productsCount || 0;
  const bannedProducts = productExclusionData?.organization?.bannedProductsCount || 0;
  const hasMostlyBannedProducts = totalProducts > 0 && (bannedProducts / totalProducts) * 100 > 90;
  return (
    <div>
      <div style={{ marginBottom: "24px " }}>
        <H2>{t("Google settings")}</H2>
      </div>
      <div style={{ marginBottom: "24px" }}>
        <Accordion header={t("Input data")}>
          {lastSuggestionRequestLoading ? (
            <SmallLoader />
          ) : (
            <>
              <SystemMessage description={tSuggestions("WarningBox")} level="alert" />
              <SuggestionInputForm
                buttonText={tSuggestions("Update")}
                category={lastSuggestionRequestData?.organization?.category}
                showExample={false}
                defaultUserInput={
                  lastSuggestionRequestData?.organization?.lastSuggestionRequestsShortHeadline?.[0]?.userInput
                }
              />
            </>
          )}
          <div style={{ marginTop: "32px" }}>
            <div style={{ marginBottom: "16px" }}>
              <H3>{t("Primary Color headline")}</H3>
            </div>
            <div style={{ marginBottom: "16px" }}>
              <Text>{t("Primary Color description")}</Text>
            </div>
            <PrimaryColor />
          </div>
          <div style={{ marginTop: "32px" }}>
            <div style={{ marginBottom: "16px" }}>
              <H3>{t("Category selection headline")}</H3>
            </div>
            <div style={{ marginBottom: "16px" }}>
              <Text>
                <Trans components={{ b: <b /> }} i18nKey="Category selection description" t={t} />
              </Text>
            </div>
            <CategorySelectionWrapper />
          </div>
        </Accordion>
      </div>
      <div style={{ marginBottom: "24px" }}>
        <Accordion defaultOpened={hasMostlyBannedProducts} header={t("Products in campaign")}>
          <SystemMessage
            level="alert"
            description={
              <Text>
                <Trans components={{ b: <b /> }} i18nKey="Update Products WarningBox" t={t} />
              </Text>
            }
          />
          <ExcludeProducts adSystem={AdSystemT.GoogleAdsT} />
        </Accordion>
      </div>
      <div style={{ marginBottom: "24px" }}>
        <Accordion header={t("Advanced Settings Headline")}>
          <SystemMessage
            level="alert"
            description={
              <Text>
                <Trans components={{ b: <b /> }} i18nKey="Advanced Settings WarningBox" t={t} />
              </Text>
            }
          />
          {!hasOptimizations && (
            <div style={{ marginBottom: "32px" }}>
              <div style={{ marginBottom: "16px" }}>
                <H3>{t("BiddingScheme")}</H3>
              </div>
              <div style={{ marginBottom: "16px" }}>
                <Text>{t("BiddingScheme Text")}</Text>
              </div>
              <BiddingScheme />
            </div>
          )}
          <div style={{ marginBottom: "32px" }}>
            <div style={{ marginBottom: "16px" }}>
              <H3>{t("AudienceSignals")}</H3>
            </div>
            <div style={{ marginBottom: "16px" }}>
              <Text>{t("AudienceSignals Text")}</Text>
            </div>
            <AudienceSignals />
          </div>

          <div style={{ marginBottom: "32px" }}>
            <div style={{ marginBottom: "16px" }}>
              <H3>{t("ExtendedConversions")}</H3>
            </div>
            <div style={{ marginBottom: "16px" }}>
              <Text>{t("ExtendedConversions Text")}</Text>
            </div>
            <ExtendedConversions />
          </div>
        </Accordion>
      </div>
      {campaignState !== "wizard" && <Deactivate adSystem={AdSystemT.GoogleAdsT} />}
    </div>
  );
};

export default GoogleSettings;
